import { Button, IconButton, Typography } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ConfirmationDialog, CustomTooltip, GoogleSignInButton, IGoogleLoginError, IGoogleLoginSuccess, IOptionType, MuiAutocompleteSelect, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import React, { useEffect, useMemo, useState } from 'react';
import { OptionType } from '../../@types/model/optionType';
import { IUser } from '../../@types/model/right/user/user';
import { useAppDispatch } from '../../@types/redux';
import { useGetSites } from '../../hooks/query/masterData/siteQueries';
import { useHandleLinkGoogleAccount, useHandleUnlinkGoogleAccount } from '../../hooks/query/user/userQueries';
import { getUserSelectedSitesLocalStorage, setUserSelectedSitesLocalStorage } from '../../service/localStorageService';
import GeneralThunks from '../../store/general/thunks';
import lodash from 'lodash';
import FreshServiceActions from '../../store/freshService/actions';

interface IUserSettingsProps {
    isOpen : boolean;
    setIsUserDialogOpenCallback : (state : boolean) => void;
    currentUser : IUser | null;
    onClose : () => void;
    signOut : () => void;
}

const UserSettings = (props : IUserSettingsProps) : React.ReactElement => {
    const dispatch = useAppDispatch();

    const [selectedSiteOptions, setSelectedSiteOptions] = useState<Array<IOptionType>>([]);
    const [isUnlinkGoogleAccountDialogOpen, setIsUnlinkGoogleAccountDialogOpen] = useState<boolean>(false);
    const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    const { isLoading: isLoadingSites, data: sites } = useGetSites();
    const { isLoading: isUnlinkingGoogleAccount, mutate: unlinkGoogleAccount } = useHandleUnlinkGoogleAccount();
    const { isLoading: isLinkingGoogleAccount, mutate: linkGoogleAccount } = useHandleLinkGoogleAccount();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (props.currentUser) {
            loadData();
        }
    }, [props.isOpen, sites]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const loadData = () : void => {
        const lastSelectedSites = getUserSelectedSitesLocalStorage();
        if (props.currentUser?.userSites && props.currentUser.userSites.length > 0) {
            const selectedSiteOptions = sites?.filter(site => site.isActive && lastSelectedSites.some(localSelectedSite => site.id === localSelectedSite.value))
                .map(OptionType.fromDataModel) ?? [];

            if (selectedSiteOptions.length < 1 && lastSelectedSites.length > 0) {
                setSelectedSiteOptions([]);
                if (lastSelectedSites.length > 0) return;
            }

            setSelectedSiteOptions(selectedSiteOptions);
        } else {
            setUserSelectedSitesLocalStorage([]);
            setSelectedSiteOptions([]); 
        }
    };

    const onGoogleAccountLinkSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        linkGoogleAccount({ code: response.code });
    };

    const onGoogleAccountLinkFailure = (response : IGoogleLoginError) : void => {
        dispatch(GeneralThunks.showErrorSnackbar({
            defaultMessage: response.error ?? 'Google Account linking failed.',
            e: response,
        }));
    };

    const onSiteChange = (value : Array<IOptionType>) : void => {
        setSelectedSiteOptions(value);
    };

    const onCloseUserSettings = () : void => {
        if (selectedSiteOptions.length === 0) {
            dispatch(GeneralThunks.showErrorSnackbar({ defaultMessage: 'Select at least one site.', e: {} }));
            return;
        }

        if (didSiteChange) {
            setIsDiscardChangesDialogOpen(true);
        } else {
            props.onClose();
        }
    };

    const onSaveUserSettings = () : void => {
        if (selectedSiteOptions.length === 0) return;

        setUserSelectedSitesLocalStorage(selectedSiteOptions);
        props.onClose();
    };

    const openUnlinkEmailDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(true);

    const closeUnlinkGoogleAccountDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(false);

    const onUnlinkGoogleAccount = () : void => {
        if (!props.currentUser)
            return;

        unlinkGoogleAccount();
        closeUnlinkGoogleAccountDialog();
    };

    const closeDiscardDialog = () : void => {
        setIsDiscardChangesDialogOpen(false);
    };

    const onDiscardChanges = () : void => {
        setIsDiscardChangesDialogOpen(false);
        props.onClose();
    };

    const onSiteSelectAll = () : void => {
        if (siteOptions.length !== selectedSiteOptions.length) {
            onSiteChange(siteOptions);
        } else {
            onSiteChange([]);
        }
    };
    
    const requestAccessToSites = () : void => {
        dispatch(FreshServiceActions.setIsSupportTicketDialogOpen(true));
        dispatch(FreshServiceActions.setSupportTicketDescription('Requesting access to assigned sites.'));
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLinkingGoogleAccount
        || isUnlinkingGoogleAccount
        || isLoadingSites;
    }, [
        isLinkingGoogleAccount,
        isUnlinkingGoogleAccount,
        isLoadingSites,
    ]);

    const siteOptions = useMemo<Array<IOptionType>>(() => {
        if (!props.currentUser?.userSites) return [];

        const userSiteIds = props.currentUser.userSites.filter(x => x.isActive).map(x => x.siteId);
        return sites?.filter(x => x.isActive && userSiteIds.includes(x.id)).map(OptionType.fromDataModel) ?? [];
        
    }, [sites, props.currentUser]);

    const didSiteChange = useMemo<boolean>(() => {
        const initialSites = getUserSelectedSitesLocalStorage();
        return !lodash.isEqual(lodash.sortBy(initialSites, 'value'), lodash.sortBy(selectedSiteOptions, 'value'));
    }, [selectedSiteOptions]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/
    const renderSiteSelection = useMemo(() => {
        if (!props.currentUser) return <div></div>;
        return (
            // TODO: Remove this eslint disabled rule once we are sure everyone have updated their sessions
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (!props.currentUser.userSites || props.currentUser.userSites.filter(x => x.isActive).length < 1)
                ? <Button className={' mt30 w300 '} variant='contained' onClick={() : void => requestAccessToSites()}>
                    {'Request sites access'}
                </Button>
                : <><MuiAutocompleteSelect
                    className={'w300 mt30'}
                    name={'site'}
                    label={'Site'}
                    isMulti={true}
                    options={siteOptions}
                    value={selectedSiteOptions}
                    onChange={onSiteChange}
                />
                <Button size={'small'} onClick={onSiteSelectAll} disabled={siteOptions.length < 1}>
                    {(siteOptions.length > 0 && siteOptions.length === selectedSiteOptions.length) ? 'Unselect All' : 'Select All'}
                </Button></>
        );
    }, [props.currentUser, siteOptions, selectedSiteOptions]);

    return (
        <ProjectDialog
            title={'User Settings'}
            isOpen={props.isOpen}
            fullWidth
            maxWidth={'xs'}
            isLoadingCircular={isLoading}
            onClose={onCloseUserSettings}
            onConfirm={onSaveUserSettings}
            disableConfirm={!didSiteChange}
        >
            <div className={'fdc aic jcc m10'}>
                {renderSiteSelection}
                <div className={'fdc aic'}>
                    {
                        (!props.currentUser?.email || !props.currentUser.profilePictureURL)
                            && <div className={'zi1'}> 
                                <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
                                    <GoogleSignInButton          
                                        onGoogleSignInSuccess={onGoogleAccountLinkSuccess}
                                        onGoogleSignInFailure={onGoogleAccountLinkFailure}   
                                        buttonText={`Link ${(!!props.currentUser?.email && !props.currentUser.profilePictureURL) ? 'Profile Picture' : 'Google Account'}`}   
                                    />
                                </GoogleOAuthProvider>
                            </div>
                    }
                    {
                        !!props.currentUser?.email
                        && <div className={'fdc aic jcc'}>
                            <div className={'fdr aic jcc'}>
                                <Typography className={'fdr m10'}>Linked Email:</Typography>
                                <Typography variant={'body1'} className={'fdr fwb cpd m10'}>{ props.currentUser.email }</Typography>
                                <CustomTooltip title={'Unlink Email'}>
                                    <IconButton className={'cr'} onClick={openUnlinkEmailDialog}>
                                        <ClearIcon />
                                    </IconButton>
                                </CustomTooltip>
                            </div>
                        </div>
                    }
                </div>
                <div className={'h10'} />
                <div className={'fdr aic pb10'}>
                    <PillButton
                        className={'h35 w150'}
                        text={'LOGOUT'}
                        color={'secondary'}
                        onClick={props.signOut}
                    />
                </div>
            </div>
            <ConfirmationDialog 
                open={isUnlinkGoogleAccountDialogOpen}
                title={'Unlink Email'}
                description={'Are you sure you would like to unlink the email address currently associated with your account?'}
                onAccept={onUnlinkGoogleAccount}
                onClose={closeUnlinkGoogleAccountDialog}
                dialogType={'orange'}
            />
            <ConfirmationDialog
                title={'Discard Changes'}
                description={'You are about to close this form. Any changes made will be lost.'}
                open={isDiscardChangesDialogOpen}
                onAccept={onDiscardChanges}
                onClose={closeDiscardDialog}
                dialogType={'orange'}
            />
        </ProjectDialog>
    );
};

export default UserSettings;